import { Link, useParams } from 'react-router-dom';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import Card from 'components/card';
import Button from 'components/button';
import SimpleTable from 'components/simpleTable';
import Header from 'components/text/header';
import useForm from 'hooks/useForm';
import InputField from 'components/fields/InputField';
import UseApi from 'hooks/useApi';
import { IoArrowBack } from 'react-icons/io5';
import WalletDetails from './walletDetails';
import TripHistory from './tripHistory';
import { useState } from 'react';
import Loader from 'components/loader';
import UserForm from './userForm';

const queryClient = new QueryClient();

type Tab = {
  id: 'user' | 'wallet' | 'trips';
  label: string;
};
const tabs: Tab[] = [
  { id: 'user', label: 'Personal Details' },
  { id: 'wallet', label: 'Wallet' },
  { id: 'trips', label: 'Bookings' },
];
interface ITabs {
  tab: Tab;
  activeTab: string;
  handleTabChange: (tab: Tab['id']) => void;
}
const TabButton: React.FC<ITabs> = ({ tab, activeTab, handleTabChange }) => (
  <button
    className={`${
      activeTab === tab.id
        ? 'border-brand-600 text-brand-600'
        : 'text-gray-500 hover:text-gray-700'
    } border-b-4 px-4 py-2 text-lg font-medium focus:outline-none`}
    onClick={() => handleTabChange(tab.id)}
  >
    {tab.label}
  </button>
);

const UserData = () => {
  const { id } = useParams();
  const { getApi, patchApi } = UseApi();
  const [activeTab, setActiveTab] = useState<'user' | 'wallet' | 'trips'>(
    'user',
  );

  const handleTabChange = (tab: 'user' | 'wallet' | 'trips') => {
    setActiveTab(tab);
  };

  async function fetchData(id: string) {
    const response = await getApi(`users/${id}`);
    return response.data;
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['data', id],
    queryFn: () => fetchData(id),
  });
  const [isFormVisible, setIsFormVisible] = useState(false);

  if (isLoading) return <Loader />;
  if (error) return <Header>An error has occurred: {error.message}</Header>;

  const {
    tripHistory = [],
    wallet = null,
    tracking = null,
    email = '',
    username = '',
    mobile = '',
    address = '',
    birthDate = null,
    picture = '',
    referrer = '',
    isAffiliate = false,
    metadata = {},
    primaryReferrer = null,
  } = data || {};

  const archive = async () => {
    await patchApi(`users/${id}/metadata`, {
      isArchived: true,
      archivedOn: new Date(),
    });
  };
  // const remove = async () => {
  //   await deleteApi(`users/${id}`);
  // };
  const toggleVisibility = () => {
    setIsFormVisible(!isFormVisible);
  };

  return (
    <div className="flex flex-col gap-3">
      <Link key={1} to={'/admin/user'}>
        <Button variant="gray" extraClasses="flex items-center gap-2 ">
          <IoArrowBack />
          Back
        </Button>
      </Link>
      <div className="flex ">
        {tabs.map((tab) => (
          <TabButton
            key={tab.id}
            tab={tab}
            activeTab={activeTab}
            handleTabChange={handleTabChange}
          />
        ))}
      </div>
      {activeTab === 'user' ? (
        <>
          <Card extra="gap-2 p-4">
            {isFormVisible ? (
              <UserForm
                type="Update"
                formValues={data}
                onSubmit={toggleVisibility}
              />
            ) : (
              <SimpleTable
                header="User Details"
                data={[
                  { key: 'Email', value: email || 'N/A' },
                  { key: 'Username', value: username || 'N/A' },
                  { key: 'Mobile', value: mobile || 'N/A' },
                  { key: 'Address', value: address || 'N/A' },
                  { key: 'Birth Date', value: birthDate?.substring(0, 10) || 'N/A' },
                  {
                    key: 'Picture',
                    value: picture ? (
                      <a href={picture} target="#">
                        {picture}
                      </a>
                    ) : 'No picture available',
                  },
                  { key: 'Referrer ID', value: referrer || 'N/A' },
                ]}
              />
            )}

            <Button extraClasses="m-auto" onClick={toggleVisibility}>
              {isFormVisible ? 'Cancel' : 'Edit'}
            </Button>
            {!metadata?.isArchived && (
              <Button extraClasses="m-auto" onClick={archive} variant="error">
                Archive
              </Button>
            )}
          </Card>
          {isAffiliate && tracking ? (
            <>
              <TrackingData tracking={tracking} primaryReferrer={primaryReferrer} />
              {tracking?.affiliateType === 'Secondary' && (
                <PrimaryAffiliateInfo primaryReferrer={primaryReferrer} />
              )}
            </>
          ) : isAffiliate && !tracking ? (
            <Card extra="m4 p-4 border-2 border-yellow-400 bg-yellow-50">
              <Header>Referral Details</Header>
              <p className="text-gray-500">No tracking data available</p>
            </Card>
          ) : (
            <AddAffiliate />
          )}
          {/* <Button extraClasses="m-auto" onClick={remove}>
          delete
        </Button> */}
        </>
      ) : activeTab === 'wallet' ? (
        <>
          {wallet ? (
            <WalletDetails wallet={wallet} />
          ) : (
            <Card extra="p-4">
              <Header>Wallet Details</Header>
              <p className="text-gray-500">No wallet information available</p>
            </Card>
          )}
        </>
      ) : (
        <>
          {tripHistory && tripHistory.length > 0 ? (
            <TripHistory tripHistory={tripHistory} />
          ) : (
            <Card extra="p-4">
              <Header>Trip History</Header>
              <p className="text-gray-500">No trip history available</p>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

const PrimaryAffiliateInfo = ({ primaryReferrer }: any) => {
  return (
    <Card extra="m4 p-4 border-2 border-green-400 bg-green-50">
      <SimpleTable
        header="Primary Affiliate Source"
        data={
          primaryReferrer ? [
            { key: 'Primary Affiliate Name', value: primaryReferrer.username || primaryReferrer.email || 'N/A' },
            { key: 'Primary Affiliate Email', value: primaryReferrer.email || 'N/A' },
            { key: 'Primary Affiliate ID', value: primaryReferrer.tracking?.referralId || 'N/A' },
            { key: 'Source', value: 'Referred by Primary Affiliate' },
          ] : [
            { key: 'Source', value: 'Direct from Website (No Primary Affiliate)' },
            { key: 'Note', value: 'This secondary affiliate signed up directly from the website' },
          ]
        }
      />
    </Card>
  );
};

const TrackingData = ({ tracking }: any) => {
  const {
    referralId = 'N/A',
    commissionPercentage = 0,
    affiliateType = 'N/A',
    amountEarned = 0,
    trackings = {},
  } = tracking || {};

  const {
    linkClicks = [],
    pageViews = [],
    signups = [],
    addToCarts = [],
    checkouts = [],
  } = trackings || {};

  return (
    <Card extra="m4 p-4 border-2 border-blue-400 bg-gray-50">
      <SimpleTable
        header="Referral Details"
        data={[
          { key: 'Referral ID', value: referralId },
          { key: 'Referral Link', value: referralId !== 'N/A' ? `https://uat.baydaarexp.com?ref=${referralId}` : 'N/A' },
          { key: 'Commission Percentage', value: `${commissionPercentage}%` },
          { key: 'Affiliate Type', value: affiliateType },
          { key: 'Amount Earned', value: `PKR ${amountEarned}` },
          { key: 'Link Clicks', value: linkClicks?.length || 0 },
          { key: 'Page Views', value: pageViews?.length || 0 },
          { key: 'Signups', value: signups?.length || 0 },
          { key: 'Add To Carts', value: addToCarts?.length || 0 },
          { key: 'Checkouts', value: checkouts?.length || 0 },
        ]}
      />
    </Card>
  );
};

const AddAffiliate = () => {
  const { id } = useParams();
  const { postApi } = UseApi();

  const addAffiliate = async () => {
    const response = await postApi(`users/${id}/affilliate`, {
      commissionPercentage,
      affiliateType: 'Primary',
    });
    if (response?.status === 200) {
      resetForm();
    }
  };

  const { values, handleChange, resetForm, handleSubmit } = useForm(
    {
      commissionPercentage: '20',
    },
    addAffiliate,
  );
  const { commissionPercentage } = values;

  return (
    <Card extra="p-4 flex flex-col gap-4 ">
      <Header>Add User as Primary Affiliate</Header>
      <InputField
        variant="auth"
        extra="mb-3"
        label="Commision Percentage*"
        placeholder="20"
        id="commissionPercentage"
        type="number"
        value={commissionPercentage}
        onChange={handleChange}
        name="commissionPercentage"
      />
      <Button extraClasses="m-auto" onClick={handleSubmit}>
        Add
      </Button>
    </Card>
  );
};

const UserDetails = (props: {}) => {
  return (
    <QueryClientProvider client={queryClient}>
      <UserData />
    </QueryClientProvider>
  );
};

export default UserDetails;